import { OmniaPromise } from "../promises";
import { IOmniaShellDialogOptions } from "../shell";
import { getAngularDependencies } from "./angular";
import { showDialog } from "./dialog";

/**
 * Definition of an Angular/React dialog, which is a React dialog
 * displayed directly by the Omnia shell, without using an Angular component as a wrapper
 */
export interface AngularReactDialog<
    Args = any,
    Deps extends { [key: string]: any } = { [key: string]: any },
    Result = any
> {
    component: React.FunctionComponent<AngularReactDialogContext<Args, Deps, Result>>;

    /**
     * Global Angular services required by the component must be defined here
     */
    inject?: Record<keyof Deps, string>;
}

/**
 * Defines the props/context for an Angular/React dialog
 */
export interface AngularReactDialogContext<
    Args = any,
    Dependencies extends { [key: string]: any } = { [key: string]: any },
    Result = any
> {
    arguments?: Args;

    dependencies: Dependencies;

    /** Call this to the component close itself */
    close(result?: Result): void;
}

/**
 * This is used to show an Angular/React dialog
 *
 * @param $injector Angular's injector, used to resolve requested dependencies
 * @param dialog Dialog defition
 * @param args Arguments, passed by the caller
 * @param options Dialog options
 * @returns A promise resolved when the dialog is closed
 */
export function showAngularReactDialog(
    $injector: ng.auto.IInjectorService,
    dialog: AngularReactDialog,
    args: any,
    options: IOmniaShellDialogOptions
): OmniaPromise<any> {
    const dependencies = getAngularDependencies($injector, dialog.inject);

    // We are omitting the "close" property here,
    // it will be added later by the showDialog() function
    const context: Omit<AngularReactDialogContext, "close"> = {
        dependencies,
        arguments: args,
    };

    return showDialog({
        content: dialog.component,
        contentProps: context,
        size: options?.size,
        cssClass: options?.windowClass,
    });
}
